import React, { useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Paper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Shared header for CMS list pages: title + actions, a compact search field,
 * a Filters button that reveals a collapsible filter panel, and refresh.
 *
 * search:        { value, onChange, onSubmit?, placeholder? }   (omit to hide the search box)
 * filterFields:  node rendered inside the collapsible panel (omit to hide the Filters button)
 * activeFilters: array of [key, value] shown as removable chips
 */
function ListToolbar({
    title,
    subtitle,
    actions,
    search,
    filterFields,
    activeFilters = [],
    onRemoveFilter,
    onApplyFilters,
    onResetFilters,
    onRefresh,
    refreshing = false,
    children
}) {
    const [open, setOpen] = useState(false);
    const hasFilters = Boolean(filterFields);

    const submitSearch = (event) => {
        event.preventDefault();
        (search?.onSubmit ?? onApplyFilters)?.();
    };

    const applyAndClose = () => {
        onApplyFilters?.();
        setOpen(false);
    };

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {actions && <Stack direction="row" spacing={1} alignItems="center">{actions}</Stack>}
            </Stack>

            <Paper
                elevation={0}
                sx={(theme) => ({
                    p: 1.25,
                    borderRadius: '16px',
                    position: 'relative',
                    overflow: 'hidden',
                    background: `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 45%, transparent 100%)`,
                    backgroundColor: theme.palette.background.paper,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: 220,
                        height: 90,
                        left: -60,
                        top: -50,
                        borderRadius: '50%',
                        background: alpha(theme.palette.primary.main, 0.22),
                        filter: 'blur(38px)',
                        pointerEvents: 'none'
                    }
                })}
            >
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ position: 'relative' }}>
                    {search && (
                        <Box component="form" onSubmit={submitSearch} sx={{ width: { xs: '100%', sm: 280 } }}>
                            <OutlinedInput
                                size="small"
                                fullWidth
                                placeholder={search.placeholder ?? 'Search…'}
                                value={search.value}
                                onChange={(e) => search.onChange(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                }
                                endAdornment={
                                    search.value ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                aria-label="Clear search"
                                                onClick={() => {
                                                    search.onChange('');
                                                    search.onClear?.();
                                                }}
                                                sx={{ p: 0.25, bgcolor: 'transparent' }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }
                                sx={{ fontSize: '0.82rem' }}
                            />
                        </Box>
                    )}

                    {hasFilters && (
                        <Badge
                            color="primary"
                            badgeContent={activeFilters.length}
                            invisible={activeFilters.length === 0}
                            sx={{ '& .MuiBadge-badge': { boxShadow: '0 0 10px currentColor' } }}
                        >
                            <Button
                                variant={open ? 'contained' : 'outlined'}
                                color={open ? 'primary' : 'inherit'}
                                size="small"
                                startIcon={<TuneIcon fontSize="small" />}
                                onClick={() => setOpen((v) => !v)}
                                aria-expanded={open}
                            >
                                Filters
                            </Button>
                        </Badge>
                    )}

                    {onRefresh && (
                        <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh'}>
                            <span>
                                <IconButton onClick={onRefresh} disabled={refreshing} aria-label="Refresh">
                                    <RefreshIcon
                                        sx={{
                                            animation: refreshing ? 'cmsSpin 0.9s linear infinite' : 'none',
                                            '@keyframes cmsSpin': { to: { transform: 'rotate(360deg)' } }
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    {children && <Box sx={{ ml: 'auto !important' }}>{children}</Box>}
                </Stack>

                {hasFilters && (
                    <Collapse in={open} unmountOnExit>
                        <Box sx={{ position: 'relative', pt: 1.5, pb: 0.5, px: 0.5 }}>
                            {filterFields}
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                <Button variant="contained" size="small" onClick={applyAndClose}>
                                    Apply filters
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        onResetFilters?.();
                                    }}
                                >
                                    Clear all
                                </Button>
                            </Stack>
                        </Box>
                    </Collapse>
                )}

                {activeFilters.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pt: 1.25, px: 0.5, position: 'relative' }}>
                        {activeFilters.map(([key, value]) => (
                            <Chip
                                key={key}
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={`${key}: ${value}`}
                                onDelete={onRemoveFilter ? () => onRemoveFilter(key) : undefined}
                                sx={{ bgcolor: 'transparent' }}
                            />
                        ))}
                    </Stack>
                )}
            </Paper>
        </Stack>
    );
}

export default ListToolbar;
